import React, { useCallback, useEffect, useState } from "react";
import { useGet, usePost } from "../../api";
import {
  COUPON_RESULT_PRESETS,
  ICouponResultWithMerchant,
  ICouponResultsResponse,
} from "../../interfaces";
import {
  Button,
  Table,
  Spinner,
  Form,
  Row,
  Col,
  Badge,
  Alert,
} from "react-bootstrap";

import { format } from "date-fns";
import { Link } from "react-router";
import { Link2 } from "react-feather";
import Select from "react-select";
import { CouponDownloadModal } from "./coupon-download-modal";
import { settingsType, useSettings } from "../../hooks";

const publishedStatusOptions = [
  { value: "0", label: "Draft" },
  { value: "1", label: "Pending" },
  { value: "2", label: "Live" },
  { value: "3", label: "Suspended" },
];

export const CouponResults: React.FC = () => {
  const [page, setPage] = React.useState(1);
  const [limit, setLimit] = React.useState(50);
  const [appliedFilter, setAppliedFilter] = useSettings(
    settingsType.COUPONS_APPLIED_FILTER
  );
  const [onlyFailed, setOnlyFailed] = useSettings(
    settingsType.COUPONS_ONLY_FAILED
  );
  const [uniqueResults, setUniqueResults] = useSettings(
    settingsType.COUPONS_UNIQUE_RESULTS
  );
  const [notApplicableFilter, setNotApplicableFilter] = useSettings(
    settingsType.COUPONS_NOT_APPLICABLE_FILTER
  );
  const [confirmedFailureFilter, setConfirmedFailureFilter] = useSettings(
    settingsType.COUPONS_CONFIRMED_FAILURE_FILTER
  );
  const [systemErrorFilter, setSystemErrorFilter] = useSettings(
    settingsType.COUPONS_SYSTEM_ERROR_FILTER
  );
  const [checkoutTechnology, setCheckoutTechnology] = useSettings(
    settingsType.COUPONS_CHECKOUT_TECHNOLOGY
  );
  const [merchantName, setMerchantName] = useSettings(
    settingsType.COUPONS_MERCHANT_NAME
  );
  const [merchantUrl, setMerchantUrl] = useSettings(
    settingsType.COUPONS_MERCHANT_URL
  );
  const [startDate, setStartDate] = useSettings(
    settingsType.COUPONS_START_DATE
  );
  const [endDate, setEndDate] = useSettings(settingsType.COUPONS_END_DATE);
  const [publishedStatusFilter, setPublishedStatusFilter] = useSettings(
    settingsType.COUPONS_PUBLISHED_STATUS
  );
  const [runTypeFilter, setRunTypeFilter] = useSettings(
    settingsType.COUPONS_RUN_TYPE
  );
  const [showDownloadModal, setShowDownloadModal] = React.useState(false);
  const [isFixing, setIsFixing] = useState(false);
  const [fixMessage, setFixMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Handler to apply confirmed failures filter preset
  /**
   * "Needs Activation": codes that are NOT live but look like they should be.
   *
   * Applied stays ALL on purpose — a code that applied cleanly is the strongest
   * reactivation case there is. This used to pin notApplicable to "true", which
   * excluded every one of them (233 rows on 30 Sep) while admitting 600 that
   * were not-applicable AND a confirmed rejection, the opposite of a candidate.
   *
   * So the two outcomes that argue AGAINST reactivating are excluded instead,
   * and everything else about the result is left open.
   */
  const applyNeedsActivationFilter = () => {
    handleClearFilters();
    // Draft, Pending, Suspended — not live, and might deserve to be.
    const p = COUPON_RESULT_PRESETS.needsActivation;
    setPublishedStatusFilter(p.publishedStatus.join(","));
    setAppliedFilter("");
    setNotApplicableFilter("");
    setConfirmedFailureFilter(String(p.confirmedFailure));
    setSystemErrorFilter(String(p.systemError));
    setStartDate(format(new Date(), "yyyy-MM-dd"));
    setEndDate("");
    setUniqueResults(true);
  };

  /**
   * "Needs Suspension": codes that are LIVE today and that the merchant
   * explicitly rejected. The QA team works this list to deactivate them.
   *
   * "Live" is the whole point and was the one filter missing. Without it the
   * view also returned confirmed failures on codes that are already suspended
   * or still in draft — nothing for anyone to action, and it buried the codes
   * that do need deactivating.
   *
   * Every filter is set explicitly, from a cleared base, so the result does not
   * depend on whatever was on screen beforehand. A leftover publishedStatus of
   * "Suspended" was enough to make this button return exactly the wrong rows.
   */
  const applyNeedsSuspensionFilter = () => {
    handleClearFilters();
    // Live, rejected outright, and not for a reason outside the code's control.
    const p = COUPON_RESULT_PRESETS.needsSuspension;
    setPublishedStatusFilter(p.publishedStatus.join(","));
    setConfirmedFailureFilter(String(p.confirmedFailure));
    setAppliedFilter(String(p.applied));
    setSystemErrorFilter(String(p.systemError));
    setNotApplicableFilter(String(p.notApplicable));
    // Today only, with no stale end date to cut the range short.
    setStartDate(format(new Date(), "yyyy-MM-dd"));
    setEndDate("");
    // One row per coupon/merchant — a code retried three times is one decision.
    setUniqueResults(true);
  };

  let queryParams = `page=${page}&limit=${limit}`;
  if (appliedFilter) queryParams += `&applied=${appliedFilter}`;
  if (onlyFailed) queryParams += `&onlyFailed=true`;
  if (uniqueResults) queryParams += `&uniqueResults=true`;
  if (notApplicableFilter)
    queryParams += `&notApplicable=${notApplicableFilter}`;
  if (confirmedFailureFilter)
    queryParams += `&confirmedFailure=${confirmedFailureFilter}`;
  if (systemErrorFilter) queryParams += `&systemError=${systemErrorFilter}`;
  if (checkoutTechnology)
    queryParams += `&checkoutTechnology=${encodeURIComponent(
      checkoutTechnology
    )}`;
  if (merchantName)
    queryParams += `&merchantName=${encodeURIComponent(merchantName)}`;
  if (merchantUrl)
    queryParams += `&merchantUrl=${encodeURIComponent(merchantUrl)}`;
  if (startDate) queryParams += `&startDate=${startDate}`;
  if (endDate) queryParams += `&endDate=${endDate}`;
  if (publishedStatusFilter)
    queryParams += `&publishedStatus=${publishedStatusFilter}`;
  if (runTypeFilter) queryParams += `&runType=${runTypeFilter}`;

  const results = useGet<ICouponResultsResponse>(
    `results/coupons?${queryParams}`,
    false // Don't fetch automatically
  );

  const fixApiCall = usePost<{ message: string }, any>();

  const handleRefresh = useCallback(async () => {
    await results.refetch();
  }, [results]);

  useEffect(() => {
    handleRefresh();
  }, [page]);

  const handleClearFilters = () => {
    setAppliedFilter("");
    setOnlyFailed(false);
    setUniqueResults(false);
    setNotApplicableFilter("");
    setConfirmedFailureFilter("");
    setSystemErrorFilter("");
    setCheckoutTechnology("");
    setMerchantName("");
    setMerchantUrl("");
    setStartDate("");
    setEndDate("");
    setPublishedStatusFilter("");
    setRunTypeFilter("");
  };

  const handleRunFix = async () => {
    if (!startDate) return;

    setIsFixing(true);
    setFixMessage(null);

    try {
      const response = await fixApiCall.execute("/fix", { startDate });

      if (response) {
        setFixMessage({ type: "success", text: response.message });
        // Refresh the results after fixing
        await handleRefresh();
      } else {
        setFixMessage({
          type: "error",
          text: "Failed to run fix",
        });
      }
    } catch (error) {
      setFixMessage({
        type: "error",
        text: "Error running fix: " + (error as Error).message,
      });
    } finally {
      setIsFixing(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Coupon Results</h2>
        <div className="d-flex align-items-center">
          <Button
            variant="outline-warning"
            className="me-2"
            onClick={handleRunFix}
            disabled={!startDate || isFixing}
            title={
              !startDate
                ? "Please select a start date to run fix"
                : "Run fix on results from start date"
            }
          >
            {isFixing ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-1"
                />
                Fixing...
              </>
            ) : (
              <>
                <i className="bi bi-wrench"></i> Fix Results
              </>
            )}
          </Button>
          <Button
            variant="outline-success"
            className="me-2"
            onClick={() => setShowDownloadModal(true)}
          >
            <i className="bi bi-download"></i> Download
          </Button>
        </div>
      </div>

      {fixMessage && (
        <Alert
          variant={fixMessage.type === "success" ? "success" : "danger"}
          dismissible
          onClose={() => setFixMessage(null)}
          className="mb-3"
        >
          {fixMessage.text}
        </Alert>
      )}

      <Row className="mb-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Merchant Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter merchant name"
              value={merchantName}
              onChange={(e) => setMerchantName(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Merchant URL</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter URL"
              value={merchantUrl}
              onChange={(e) => setMerchantUrl(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>Applied Status</Form.Label>
            <Form.Select
              value={appliedFilter}
              onChange={(e) => setAppliedFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="true">Applied</option>
              <option value="false">Not Applied</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>End Date</Form.Label>
            <Form.Control
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={2}>
          <Form.Group>
            <Form.Label>Confirmed Failure</Form.Label>
            <Form.Select
              value={confirmedFailureFilter}
              onChange={(e) => setConfirmedFailureFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>System Error</Form.Label>
            <Form.Select
              value={systemErrorFilter}
              onChange={(e) => setSystemErrorFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>Run Type</Form.Label>
            <Form.Select
              value={runTypeFilter}
              onChange={(e) => setRunTypeFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="live">Live</option>
              <option value="audit">Audit</option>
              <option value="capture">Capture</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>Not Applicable</Form.Label>
            <Form.Select
              value={notApplicableFilter}
              onChange={(e) => setNotApplicableFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Checkout Technology</Form.Label>
            <Form.Select
              onChange={(e) => setCheckoutTechnology(e.target.value)}
            >
              <option value="">All</option>
              <option value="shopify">Shopify</option>
              <option value="woocommerce">WooCommerce</option>
              <option value="magento">Magento</option>
              <option value="bigcommerce">BigCommerce</option>
              <option value="other">Other</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={5}>
          <Form.Group>
            <Form.Label>Published Status</Form.Label>
            <Select
              isMulti
              options={publishedStatusOptions}
              value={publishedStatusOptions.filter((opt) =>
                publishedStatusFilter
                  ? publishedStatusFilter.split(",").includes(opt.value)
                  : false
              )}
              onChange={(selected) =>
                setPublishedStatusFilter(selected.map((s) => s.value).join(","))
              }
              placeholder="All"
              isClearable
              styles={{
                control: (base) => ({ ...base, minHeight: "38px" }),
              }}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={4}>
          <Form.Check
            type="checkbox"
            label="Show only coupons from failed tests"
            checked={onlyFailed}
            onChange={(e) => setOnlyFailed(e.target.checked)}
          />
        </Col>
        <Col md={4}>
          <Form.Check
            type="checkbox"
            label="Show only unique results (most recent per coupon/merchant)"
            checked={uniqueResults}
            onChange={(e) => setUniqueResults(e.target.checked)}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <Button
            variant="primary"
            size="sm"
            onClick={handleRefresh}
            className="me-2"
          >
            <i className="bi bi-search"></i> Search / Apply Filters
          </Button>
          <Button
            variant="outline-warning"
            size="sm"
            onClick={applyNeedsSuspensionFilter}
            className="me-2"
          >
            <i className="bi bi-pause-circle"></i> Needs Suspension (Today)
          </Button>
          <Button
            variant="outline-info"
            size="sm"
            onClick={applyNeedsActivationFilter}
            className="me-2"
          >
            <i className="bi bi-play-circle"></i> Needs Activation (Today)
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleClearFilters}
          >
            <i className="bi bi-x-circle"></i> Clear Filters
          </Button>
          {results.data?.pagination && (
            <span className="ms-3 text-muted">
              Showing {results.data.results.length} of{" "}
              {results.data.pagination.total} results
            </span>
          )}
        </Col>
      </Row>

      <Table striped bordered hover size="sm">
        <thead>
          <tr>
            <th>Coupon Code</th>
            <th>Merchant</th>
            <th>Applied</th>
            <th>Savings</th>
            <th>Flags</th>
            <th>Error</th>
            <th>Test Date</th>
          </tr>
        </thead>
        <tbody>
          {results.loading ? (
            <tr>
              <td colSpan={7} className="text-center py-4">
                <Spinner animation="border" role="status" className="me-2">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
                Loading coupon results...
              </td>
            </tr>
          ) : results.error ? (
            <tr>
              <td colSpan={7} className="text-center text-danger py-4">
                Error loading coupon results: {results.error.message}
              </td>
            </tr>
          ) : !results.data ? (
            <tr>
              <td colSpan={7} className="text-center py-4">
                <div className="text-muted">
                  <i className="bi bi-info-circle me-2"></i>
                  Click "Search / Apply Filters" to load coupon results
                </div>
              </td>
            </tr>
          ) : results?.data?.results?.length === 0 ? (
            <tr>
              <td colSpan={7} className="text-center py-4">
                No coupon results found
              </td>
            </tr>
          ) : (
            results?.data?.results?.map((result: ICouponResultWithMerchant) => {
              return (
                <tr key={result._id}>
                  <td>
                    <Badge
                      bg="dark"
                      className="fs-6"
                      style={{ cursor: "pointer" }}
                      onClick={() => navigator.clipboard.writeText(result.code)}
                      title="Click to copy code"
                    >
                      <span>{result.code}</span>
                    </Badge>
                    {result.publishedStatus != null && (
                      <Badge
                        bg={
                          result.publishedStatus === 2
                            ? "success"
                            : result.publishedStatus === 1
                              ? "warning"
                              : result.publishedStatus === 3
                                ? "danger"
                                : "secondary"
                        }
                        className="ms-1"
                        title={`Source: ${result.source || "unknown"}`}
                      >
                        {result.publishedStatus === 2
                          ? "Live"
                          : result.publishedStatus === 1
                            ? "Pending"
                            : result.publishedStatus === 3
                              ? "Suspended"
                              : "Draft"}
                      </Badge>
                    )}
                  </td>
                  <td>
                    <Link
                      to={result.merchant.homePageUrl}
                      target="_blank"
                      className="me-2"
                    >
                      <Link2 size={14} />
                    </Link>
                    <Link to={`/merchants/${result.merchant._id}`}>
                      <strong>{result.merchant.name}</strong>
                    </Link>
                    <div className="mt-1">
                      <Badge bg="secondary" className="me-1">
                        {result.merchant.countryCode}
                      </Badge>
                      {result.merchant.checkoutType && (
                        <Badge bg="info" className="text-dark">
                          {result.merchant.checkoutType}
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td>
                    {result.applied ? (
                      <Badge bg="success">
                        <i className="bi bi-check-circle"></i> Applied
                      </Badge>
                    ) : result.notApplicableToBasket ? (
                      <Badge
                        bg="warning"
                        title="Coupon not applicable to basket"
                      >
                        <i className="bi bi-exclamation-circle"></i> Not
                        Applicable
                      </Badge>
                    ) : (
                      <Badge bg="danger">
                        <i className="bi bi-x-circle"></i> Not Applied
                      </Badge>
                    )}
                  </td>
                  <td>
                    {result.savings > 0 ? (
                      <div>
                        {result.percentageSavings && (
                          <div
                            title={`${
                              result.testResult.currency
                            } ${result.savings.toFixed(2)} savings`}
                          >
                            <small className="text-success">
                              ({result.percentageSavings.toFixed(1)}%)
                            </small>
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted" title="No savings">
                        N/A
                      </span>
                    )}
                  </td>
                  <td>
                    {result.confirmedFailure && (
                      <Badge
                        bg="danger"
                        className="me-1"
                        title="Confirmed Failure"
                      >
                        <i className="bi bi-exclamation-triangle"></i> CF
                      </Badge>
                    )}
                    {result.systemError && (
                      <Badge bg="warning" title="System Error">
                        <i className="bi bi-bug"></i> SE
                      </Badge>
                    )}
                    {!result.confirmedFailure && !result.systemError && (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>
                    {result.errorMessage ? (
                      <small
                        className="text-danger"
                        title={result.errorMessage}
                      >
                        {result.errorMessage}
                      </small>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>
                    <small>
                      {format(
                        new Date(result.testResult.startedAt),
                        "yyyy-MM-dd HH:mm"
                      )}
                    </small>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
        {results.data?.pagination && (
          <tfoot>
            <tr>
              <td colSpan={7} className="text-center">
                <div className="d-flex justify-content-center align-items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage(1)}
                    disabled={page <= 1}
                    title="First page"
                  >
                    <i className="bi bi-chevron-double-left"></i>
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage(page - 1)}
                    disabled={page <= 1}
                    title="Previous page"
                  >
                    <i className="bi bi-chevron-left"></i>
                  </Button>
                  <span className="mx-2">
                    Page
                    <Form.Select
                      size="sm"
                      value={page}
                      onChange={(e) => setPage(Number(e.target.value))}
                      className="d-inline-block mx-2"
                      style={{ width: "auto" }}
                    >
                      {Array.from(
                        { length: results.data.pagination.totalPages },
                        (_, i) => i + 1
                      ).map((pageNum) => (
                        <option key={pageNum} value={pageNum}>
                          {pageNum}
                        </option>
                      ))}
                    </Form.Select>
                    of {results.data.pagination.totalPages}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={page >= results.data.pagination.totalPages}
                    title="Next page"
                  >
                    <i className="bi bi-chevron-right"></i>
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      results.data &&
                      setPage(results.data.pagination.totalPages)
                    }
                    disabled={page >= results.data.pagination.totalPages}
                    title="Last page"
                  >
                    <i className="bi bi-chevron-double-right"></i>
                  </Button>
                </div>
              </td>
            </tr>
          </tfoot>
        )}
      </Table>

      <CouponDownloadModal
        show={showDownloadModal}
        onHide={() => setShowDownloadModal(false)}
        onSuccess={() => handleRefresh()}
        appliedFilter={appliedFilter}
        onlyFailed={onlyFailed}
        uniqueResults={uniqueResults}
        notApplicable={notApplicableFilter}
        confirmedFailure={confirmedFailureFilter}
        systemError={systemErrorFilter}
        checkoutTechnology={checkoutTechnology}
        merchantName={merchantName}
        merchantUrl={merchantUrl}
        startDate={startDate}
        endDate={endDate}
        publishedStatus={publishedStatusFilter}
        runType={runTypeFilter}
      />
    </div>
  );
};
